import React, { useEffect, useMemo, useState } from "react";
import styles from "./FeatureModal.module.css";

/** Color choices for feature bars */
const PALETTE = [
  "#EF4444", // red
  "#F59E0B", // amber
  "#10B981", // emerald
  "#3B82F6", // blue
  "#8B5CF6", // violet
  "#F472B6", // pink
  "#F97316", // orange
  "#22D3EE", // cyan
];

/**
 * Modal for creating a new feature from a current selection.
 * Props:
 *  - isOpen: boolean
 *  - onClose: fn()
 *  - onSubmit: fn({ name, color, direction, strand })
 *  - selection: {start, end} | null
 */
export default function FeatureModal({ isOpen, onClose, onSubmit, selection }) {
  //#region Derived selection info
  const hasSelection =
    !!selection &&
    Number.isInteger(selection.start) &&
    Number.isInteger(selection.end);

  const selectedLengthBp = useMemo(() => {
    if (!hasSelection) return 0;
    const s = Math.min(selection.start, selection.end);
    const e = Math.max(selection.start, selection.end);
    return e - s + 1;
  }, [selection, hasSelection]);
  //#endregion

  //#region Local form state
  const [featureName, setFeatureName] = useState("");
  const [featureColor, setFeatureColor] = useState(PALETTE[3]);
  /** @type {"forward"|"reverse"|"none"} */
  const [arrowDirection, setArrowDirection] = useState("forward");
  /** @type {"top"|"bottom"} */
  const [strandSide, setStrandSide] = useState("top");
  //#endregion

  //#region Reset on open
  useEffect(() => {
    if (!isOpen) return;
    setFeatureName("");
    setFeatureColor(PALETTE[3]);
    setArrowDirection("forward");
    setStrandSide("top");
  }, [isOpen]);
  //#endregion

  if (!isOpen) return null;

  //#region Render
  return (
    <div
      className={styles.root}
      role="dialog"
      aria-modal="true"
      aria-labelledby="feat-title"
    >
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.panel}>
        {/* Header */}
        <div className={styles.header}>
          <h2 id="feat-title" className={styles.title}>
            Add feature from selection
          </h2>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {!hasSelection ? (
            <div className={styles.noSel}>
              Make a selection first to define feature coordinates.
            </div>
          ) : (
            <div className={styles.summary}>
              Selected: <b>{selectedLengthBp}</b> bp (positions{" "}
              <b>{Math.min(selection.start, selection.end) + 1}</b>–
              <b>{Math.max(selection.start, selection.end) + 1}</b>)
            </div>
          )}

          {/* Name */}
          <label className={styles.field}>
            <span className={styles.label}>Name</span>
            <input
              className={styles.input}
              value={featureName}
              onChange={(e) => setFeatureName(e.target.value)}
              placeholder="e.g. Promoter"
            />
          </label>

          {/* Color picker */}
          <div className={styles.colors}>
            <span className={styles.label}>Color</span>
            <div className={styles.swatchRow}>
              {PALETTE.map((c) => (
                <button
                  key={c}
                  className={styles.swatch}
                  style={{
                    background: c,
                    outline: c === featureColor ? "2px solid #fff" : "none",
                  }}
                  onClick={() => setFeatureColor(c)}
                  type="button"
                  aria-label={`Pick color ${c}`}
                />
              ))}
            </div>
          </div>

          {/* Direction & Strand */}
          <div className={styles.row}>
            <label className={styles.field}>
              <span className={styles.label}>Arrow direction</span>
              <div className={styles.segment}>
                <button
                  className={`${styles.segmentBtn} ${
                    arrowDirection === "reverse" ? styles.active : ""
                  }`}
                  onClick={() => setArrowDirection("reverse")}
                  type="button"
                  title="Reverse (←)"
                >
                  ←
                </button>
                <button
                  className={`${styles.segmentBtn} ${
                    arrowDirection === "none" ? styles.active : ""
                  }`}
                  onClick={() => setArrowDirection("none")}
                  type="button"
                  title="None"
                >
                  ·
                </button>
                <button
                  className={`${styles.segmentBtn} ${
                    arrowDirection === "forward" ? styles.active : ""
                  }`}
                  onClick={() => setArrowDirection("forward")}
                  type="button"
                  title="Forward (→)"
                >
                  →
                </button>
              </div>
            </label>

            <label className={styles.field}>
              <span className={styles.label}>Strand</span>
              <div className={styles.segment}>
                <button
                  className={`${styles.segmentBtn} ${
                    strandSide === "top" ? styles.active : ""
                  }`}
                  onClick={() => setStrandSide("top")}
                  type="button"
                >
                  Top
                </button>
                <button
                  className={`${styles.segmentBtn} ${
                    strandSide === "bottom" ? styles.active : ""
                  }`}
                  onClick={() => setStrandSide("bottom")}
                  type="button"
                >
                  Bottom
                </button>
              </div>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.cancel} onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className={styles.ok}
            onClick={() =>
              onSubmit?.({
                // map back to original keys to keep external API unchanged
                name: featureName,
                color: featureColor,
                direction: arrowDirection,
                strand: strandSide,
              })
            }
            type="button"
            disabled={!hasSelection}
            title={!hasSelection ? "Select a range first" : "Add feature"}
          >
            Add feature
          </button>
        </div>
      </div>
    </div>
  );
  //#endregion
}
